// Global Instructions Rule Applied!
//
// Fees and expenses outside the commitment (spec A3.9 addendum part 4). A
// fund's terms say whether its commitments cover the management fees and
// the expenses (funds.commitment_includes_mgmt_fees / _expenses; unset =
// included). Lines that are outside do not count toward % Drawn / Uncalled
// and, on a % of committed call, are added on top of the %.
//
// The server decides (capital_calls__item_bucket /
// capital_calls__counts_toward_commitment); this file only words the result
// for the screens and mirrors the line classification for the call form's
// live preview of unsaved rows.

/** The line types a fund's terms keep outside the commitment, in words. */
export function outsideCommitmentLabel(includesMgmtFees: boolean, includesExpenses: boolean): string | null {
  if (!includesMgmtFees && !includesExpenses) return 'management fees and expenses'
  if (!includesMgmtFees) return 'management fees'
  if (!includesExpenses) return 'expenses'
  return null
}

/**
 * The help "?" on a % Drawn / Uncalled header: which line types are left
 * out under the fund's terms. Null when everything counts (no help shown).
 * A flag the server did not send counts as included, like the terms do.
 */
export function outsideCommitmentHelp(
  includesMgmtFees: boolean | null | undefined,
  includesExpenses: boolean | null | undefined
): string | null {
  const outside = outsideCommitmentLabel(includesMgmtFees ?? true, includesExpenses ?? true)
  if (!outside) return null
  return `Under this fund's terms ${outside} are outside the commitment: they are called on top of it and do not count toward % drawn or Uncalled. Called to date is the total cash called, including them.`
}

export type CallLineBucket = 'investment' | 'expenses' | 'fees'

/** The terms block cc__call_detail sends on the call (prog__commitment_terms). */
export interface CommitmentTerms {
  pct_sized?: boolean | null
  includes_mgmt_fees?: boolean | null
  includes_expenses?: boolean | null
  fees_item_type_id?: string | null
  expenses_item_type_id?: string | null
  combined_item_type_id?: string | null
}

/**
 * Bucket of a call line, the client mirror of capital_calls__item_bucket:
 * a fee-period line or the fees item is fees, the expenses item is
 * expenses, everything else (the balance, contribution catch-up lines, the
 * combined investments-and-expenses item) is investment.
 */
export function callLineBucket(
  line: { item_type_id?: string | null; isFeePeriodLine?: boolean },
  terms: CommitmentTerms | null | undefined
): CallLineBucket {
  if (line.isFeePeriodLine) return 'fees'
  if (line.item_type_id && terms?.fees_item_type_id && line.item_type_id === terms.fees_item_type_id) return 'fees'
  if (line.item_type_id && terms?.expenses_item_type_id && line.item_type_id === terms.expenses_item_type_id)
    return 'expenses'
  return 'investment'
}

/** Whether a bucket counts toward the commitment under the fund's terms. */
export function bucketCountsTowardCommitment(
  bucket: CallLineBucket,
  terms: CommitmentTerms | null | undefined
): boolean {
  if (bucket === 'fees') return terms?.includes_mgmt_fees ?? true
  if (bucket === 'expenses') return terms?.includes_expenses ?? true
  return true
}
